import { IMovimentacaoListItem } from '../../models/movimentacoes.service.model';
import {
    IMovimentacaoGroupedListGroupView,
    IMovimentacaoGroupedListRowView,
    TMovimentacaoListDisplayMode
} from '../components/listagem/movimentacoes-listagem.model';
import { IMovimentacaoOrderingOption } from '../models/movimentacoes.index.model';

/**
 * Agrupa a lista carregada da index por colaborador ou rubrica.
 */
export function buildMovimentacaoGroupedListGroups(
    entities: IMovimentacaoListItem[],
    displayMode: TMovimentacaoListDisplayMode
): IMovimentacaoGroupedListGroupView[] {
    if (displayMode === 'flat' || !Array.isArray(entities) || !entities.length) {
        return [];
    }

    const groups = new Map<string, IMovimentacaoGroupedListGroupView>();

    entities.forEach((entity) => {
        const key = resolveMovimentacaoGroupKey(entity, displayMode);

        if (!groups.has(key)) {
            groups.set(key, {
                id: `${displayMode}:${key}`,
                title: resolveMovimentacaoGroupTitle(entity, displayMode),
                subtitle: resolveMovimentacaoGroupSubtitle(entity, displayMode),
                metaLabel: resolveMovimentacaoGroupMetaLabel(entity, displayMode),
                representativeEntity: entity,
                rows: []
            });
        }

        const group = groups.get(key)!;
        const row: IMovimentacaoGroupedListRowView = {
            id: entity.movimentacao,
            entity
        };

        group.rows.push(row);
    });

    return Array.from(groups.values());
}

/**
 * Resolve a opção de ordenação fixa necessária para manter o agrupamento estável.
 */
export function resolveMovimentacaoDisplayModeOrderingOption(
    displayMode: TMovimentacaoListDisplayMode,
    orderingOptions: IMovimentacaoOrderingOption[]
): IMovimentacaoOrderingOption | null {
    if (displayMode === 'group_by_trabalhador') {
        return orderingOptions.find((option) => option.value === 'trabalhador') || null;
    }

    if (displayMode === 'group_by_evento') {
        return orderingOptions.find((option) => option.value === 'evento') || null;
    }

    return null;
}

function resolveMovimentacaoGroupKey(
    entity: IMovimentacaoListItem,
    displayMode: TMovimentacaoListDisplayMode
): string {
    if (displayMode === 'group_by_trabalhador') {
        return entity?.trabalhador?.trabalhador || entity?.trabalhador?.codigo || entity.movimentacao;
    }

    return entity?.evento?.evento || entity?.evento?.codigo || entity.movimentacao;
}

function resolveMovimentacaoGroupTitle(
    entity: IMovimentacaoListItem,
    displayMode: TMovimentacaoListDisplayMode
): string {
    if (displayMode === 'group_by_trabalhador') {
        return entity?.trabalhador?.nome || entity?.trabalhador?.codigo || '-';
    }

    const nome = entity?.evento?.nome ? `${entity.evento.nome}`.trim() : '';
    const codigo = entity?.evento?.codigo ? `${entity.evento.codigo}`.trim() : '';
    return nome || codigo || '-';
}

function resolveMovimentacaoGroupSubtitle(
    entity: IMovimentacaoListItem,
    displayMode: TMovimentacaoListDisplayMode
): string {
    if (displayMode === 'group_by_trabalhador') {
        const codigo = entity?.trabalhador?.codigo ? `${entity.trabalhador.codigo}`.trim() : '';
        return codigo ? `Matrícula: ${codigo}` : '';
    }

    const codigo = entity?.evento?.codigo ? `${entity.evento.codigo}`.trim() : '';
    return codigo ? `Código: ${codigo}` : '';
}

function resolveMovimentacaoGroupMetaLabel(
    entity: IMovimentacaoListItem,
    displayMode: TMovimentacaoListDisplayMode
): string {
    if (displayMode !== 'group_by_evento') {
        return '';
    }

    const tipoValor = Number(entity?.evento?.tipovalor);
    if (tipoValor === 0) {
        return 'Rendimento';
    }

    if (tipoValor === 1) {
        return 'Desconto';
    }

    return '';
}
